import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "@/lib/api";
import type { User } from "@/types/api";

const KEY = "users";

export function useUsers() {
  return useQuery({
    queryKey: [KEY],
    queryFn: async () => (await api.get<User[]>("/api/users")).data,
  });
}

export interface CreateUserInput {
  email: string;
  name: string;
  password: string;
}

export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateUserInput) =>
      (await api.post<User>("/api/users", input)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
}

export interface UpdateUserInput {
  id: string;
  name?: string;
  is_enabled?: boolean;
}

export function useUpdateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...patch }: UpdateUserInput) =>
      (await api.patch<User>(`/api/users/${id}`, patch)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
}

export function useResetUserPassword() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, new_password }: { id: string; new_password: string }) =>
      (await api.post<User>(`/api/users/${id}/reset-password`, { new_password })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: [KEY] }),
  });
}
